import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Add Missed Punch Dialog Component
 *
 * Allows administrators to add missed punches for employees.
 * Requires a reason for audit trail compliance.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { X, AlertTriangle, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { config } from '../../lib/config';
import { useAuthStore } from '../../stores/auth.store';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
const TIME_API_URL = config.api.time;
const actionOptions = [
    { value: 'CLOCK_IN', label: 'Clock In', color: 'bg-green-100 text-green-800 border-green-300' },
    { value: 'CLOCK_OUT', label: 'Clock Out', color: 'bg-red-100 text-red-800 border-red-300' },
    { value: 'BREAK_START', label: 'Break Start', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    { value: 'BREAK_END', label: 'Break End', color: 'bg-brand-100 text-brand-800 border-brand-300' },
];
async function createMissedPunch(data) {
    const tokens = useAuthStore.getState().tokens;
    const response = await fetch(`${TIME_API_URL}/punches`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens?.idToken || ''}`,
        },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to create punch: ${response.statusText}`);
    }
}
export function AddMissedPunchDialog({ employeeId, employeeName, onClose, onSuccess }) {
    const queryClient = useQueryClient();
    // Default to yesterday at 9 AM
    const defaultDate = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');
    const [action, setAction] = useState('CLOCK_IN');
    const [date, setDate] = useState(defaultDate);
    const [time, setTime] = useState('09:00');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const mutation = useMutation({
        mutationFn: (data) => createMissedPunch(data),
        onSuccess: () => {
            toast.success('Missed Punch Added', {
                description: `${actionOptions.find(a => a.value === action)?.label} has been recorded.`,
            });
            // Invalidate punch history queries to refresh the data
            void queryClient.invalidateQueries({ queryKey: ['punchHistory'] });
            void queryClient.invalidateQueries({ queryKey: ['punch-history'] });
            onSuccess?.();
            onClose();
        },
        onError: (error) => {
            toast.error('Failed to Add Punch', {
                description: error.message,
            });
        },
    });
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!reason.trim() || reason.trim().length < 10) {
            toast.error('Reason Required', {
                description: 'Please provide a reason for this missed punch (minimum 10 characters).',
            });
            return;
        }
        // Combine date and time into ISO timestamp
        const timestamp = new Date(`${date}T${time}:00`).toISOString();
        // Validate not in the future
        if (new Date(timestamp) > new Date()) {
            toast.error('Invalid Time', {
                description: 'Cannot add a punch in the future.',
            });
            return;
        }
        mutation.mutate({
            action,
            employeeId,
            timestamp,
            isMissedPunch: true,
            missedPunchReason: reason.trim(),
            ...(notes.trim() && { notes: notes.trim() }),
        });
    };
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4", children: _jsxs("div", { className: "relative w-full max-w-md rounded-lg bg-white shadow-xl", children: [_jsxs("div", { className: "flex items-center justify-between border-b px-6 py-4", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "rounded-full bg-green-100 p-2", children: _jsx(Plus, { className: "h-5 w-5 text-green-600" }) }), _jsxs("div", { children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900", children: "Add Missed Punch" }), _jsx("p", { className: "text-sm text-gray-500", children: employeeName })] })] }), _jsx("button", { onClick: onClose, disabled: mutation.isPending, className: "rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50", children: _jsx(X, { className: "h-5 w-5" }) })] }), _jsxs("form", { onSubmit: handleSubmit, className: "p-6 space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { children: "Punch Type" }), _jsx("div", { className: "grid grid-cols-2 gap-2", children: actionOptions.map((opt) => (_jsx("button", { type: "button", onClick: () => setAction(opt.value), className: `px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all ${action === opt.value
                                            ? `${opt.color} border-current`
                                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300'}`, children: opt.label }, opt.value))) })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "date", children: "Date" }), _jsx(Input, { id: "date", type: "date", value: date, onChange: (e) => setDate(e.target.value), max: format(new Date(), 'yyyy-MM-dd'), disabled: mutation.isPending, required: true })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "time", children: "Time" }), _jsx(Input, { id: "time", type: "time", value: time, onChange: (e) => setTime(e.target.value), disabled: mutation.isPending, required: true })] }), _jsxs("div", { className: "space-y-2", children: [_jsxs(Label, { htmlFor: "reason", children: ["Reason for Missed Punch ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx(Textarea, { id: "reason", value: reason, onChange: (e) => setReason(e.target.value), placeholder: "e.g., Employee forgot to clock in, system was down...", disabled: mutation.isPending, rows: 2, required: true, minLength: 10 }), _jsx("p", { className: "text-xs text-gray-500", children: "Minimum 10 characters required" })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "notes", children: "Additional Notes (Optional)" }), _jsx(Input, { id: "notes", value: notes, onChange: (e) => setNotes(e.target.value), placeholder: "Any additional context...", disabled: mutation.isPending })] }), _jsxs("div", { className: "flex items-start gap-2 rounded-lg bg-yellow-50 border border-yellow-200 p-3", children: [_jsx(AlertTriangle, { className: "h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" }), _jsx("p", { className: "text-xs text-yellow-800", children: "This will add a punch record marked as a missed punch. The action will be recorded with your user ID for audit purposes." })] }), _jsxs("div", { className: "flex gap-3 pt-2", children: [_jsx(Button, { type: "button", variant: "outline", onClick: onClose, disabled: mutation.isPending, className: "flex-1", children: "Cancel" }), _jsx(Button, { type: "submit", disabled: mutation.isPending || !reason.trim() || reason.trim().length < 10, className: "flex-1 bg-green-600 hover:bg-green-700", children: mutation.isPending ? 'Adding...' : 'Add Punch' })] })] })] }) }));
}
